import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, or } from 'firebase/firestore';
import SectionTitle from './SectionTitle';
import { useAfiliado } from '../context/AfiliadoContext';

// ---------- Utilidades de imagen ----------

const getOptimizedUrl = (url, width = 600) => {
  if (!url || !url.includes('cloudinary.com') || !url.includes('/upload/')) return url;
  const [base, resto] = url.split('/upload/');
  const segmentos = resto.split('/');
  const esTransformacion = /(^|,)(w_|h_|c_|q_|f_|e_|g_|b_|ar_|dpr_|fl_)/.test(segmentos[0]);
  if (esTransformacion) segmentos.shift();
  return `${base}/upload/w_${width},c_limit,q_auto,f_auto/${segmentos.join('/')}`;
};

const FALLBACK_IMG =
  'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect width="300" height="300" fill="%23f5f5f3"/%3E%3C/svg%3E';

// ---------- Utilidades de fecha ----------

const aFecha = (campo) => {
  if (!campo) return null;
  if (typeof campo.toDate === 'function') return campo.toDate();
  if (typeof campo.seconds === 'number')  return new Date(campo.seconds * 1000);
  if (typeof campo._seconds === 'number') return new Date(campo._seconds * 1000);
  const d = new Date(campo);
  return isNaN(d.getTime()) ? null : d;
};

const fechaEfectiva = (combo) => {
  const actualizacion = aFecha(combo.fechaActualizacion);
  const creacion      = aFecha(combo.fechaCreacion);
  if (actualizacion && creacion) {
    return actualizacion.getTime() > creacion.getTime() ? actualizacion : creacion;
  }
  return actualizacion || creacion || null;
};

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const tituloDelMes = () => `Combos y promociones de ${MESES[new Date().getMonth()]}`;

const limpiarDescripcion = (texto, max = 90) => {
  if (!texto) return '';
  const limpia = texto.replace(/<br\s*\/?>/gi, ' ').replace(/\s+/g, ' ').trim();
  return limpia.length > max ? limpia.substring(0, max).trimEnd() + '…' : limpia;
};

// ---------- Tarjeta de combo ----------

const ComboCard = ({ combo, index, onClick }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError]   = useState(false);

  const srcOptimizado = imgError
    ? FALLBACK_IMG
    : getOptimizedUrl(combo.imagenUrl || combo.imagen, 600) || FALLBACK_IMG;

  return (
    <div className="combo-card" onClick={onClick}>

      {/* Imagen cuadrada */}
      <div className="combo-img-wrap">
        {!imgLoaded && <div className="combo-skeleton" />}
        <img
          className="combo-img"
          src={srcOptimizado}
          alt={combo.nombre}
          loading={index < 3 ? 'eager' : 'lazy'}
          fetchpriority={index < 3 ? 'high' : 'auto'}
          decoding="async"
          onLoad={() => setImgLoaded(true)}
          onError={() => { setImgError(true); setImgLoaded(true); }}
          style={{ opacity: imgLoaded ? 1 : 0 }}
        />
      </div>

      {/* Franja sólida con el nombre */}
      <div className="combo-banda">
        <span className="combo-banda-nombre">{combo.nombre}</span>
      </div>

      {/* Contenido bajo la franja: solo descripción y conoce más */}
      <div className="combo-body">
        {combo.descripcion && (
          <p className="combo-desc">{limpiarDescripcion(combo.descripcion)}</p>
        )}

        <span className="combo-cta">
          Conoce más
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6"/>
          </svg>
        </span>
      </div>
    </div>
  );
};

// ---------- Componente principal ----------

const Combos = ({ addToCart }) => {
  const navigate = useNavigate();
  const { afiliado, vistaAfiliado } = useAfiliado();
  const esAfiliado = Boolean(afiliado && vistaAfiliado);

  const [combos, setCombos]                 = useState([]);
  const [loading, setLoading]               = useState(true);
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hayOverflow, setHayOverflow]       = useState(false);
  const scrollRef                           = useRef(null);
  const isPausedRef                         = useRef(false);

  useEffect(() => {
    const obtenerCombos = async () => {
      try {
        setLoading(true);
        const productosRef = collection(db, 'catalogo');
        const q = query(
          productosRef,
          or(
            where('categoria', '==', 'Combos'),
            where('categorias', 'array-contains', 'Combos'),
            where('tiposUsuario', 'array-contains', 'Combos'),
            where('categoria', '==', 'combos'),
            where('categorias', 'array-contains', 'combos')
          )
        );

        const snap = await getDocs(q);
        let docs = [];
        snap.forEach((doc) => docs.push({ id: doc.id, ...doc.data() }));

        let visibles = docs.filter(p => p.visible !== false);
        visibles = visibles
          .map((combo) => ({ combo, fecha: fechaEfectiva(combo) }))
          .sort((a, b) => {
            if (!a.fecha && !b.fecha) return 0;
            if (!a.fecha) return 1;
            if (!b.fecha) return -1;
            return b.fecha.getTime() - a.fecha.getTime();
          })
          .map((x) => x.combo);

        setCombos(visibles);
      } catch (error) {
        console.error('Error obteniendo combos:', error);
      } finally {
        setLoading(false);
      }
    };
    obtenerCombos();
  }, []);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const tieneOverflow = el.scrollWidth > el.clientWidth + 4;
    setHayOverflow(tieneOverflow);
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(tieneOverflow && (el.scrollLeft + el.clientWidth < el.scrollWidth - 10));
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkScroll) : null;
    if (ro) ro.observe(el);

    const timer = setTimeout(checkScroll, 150);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
      if (ro) ro.disconnect();
      clearTimeout(timer);
    };
  }, [checkScroll, combos]);

  // ── Autoplay suave y desfasado (cada 6.8s) con pausa en interacción ───
  useEffect(() => {
    if (!hayOverflow || combos.length <= 1) return;

    const interval = setInterval(() => {
      if (isPausedRef.current) return;
      const el = scrollRef.current;
      if (!el) return;

      const card = el.querySelector('.combo-card');
      const gap = window.innerWidth <= 640 ? 16 : 24;
      const step = card ? card.offsetWidth + gap : 250;

      // Si llegó al final, volver al inicio
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 15) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: step, behavior: 'smooth' });
      }
    }, 6800);

    return () => clearInterval(interval);
  }, [hayOverflow, combos]);

  const scrollManual = (direction) => {
    const container = scrollRef.current;
    if (!container) return;
    const card = container.querySelector('.combo-card');
    const amount = card ? card.offsetWidth + 16 : 250;
    container.scrollBy({ left: direction === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  if (loading || combos.length === 0) return null;

  return (
    <div style={{ width: '100%', marginBottom: 0 }}>

      {/* Título dinámico estandarizado */}
      <SectionTitle>{tituloDelMes()}</SectionTitle>

      <div
        style={{ position: 'relative' }}
        onMouseEnter={() => { isPausedRef.current = true; }}
        onMouseLeave={() => { isPausedRef.current = false; }}
        onTouchStart={() => { isPausedRef.current = true; }}
        onTouchEnd={() => { setTimeout(() => { isPausedRef.current = false; }, 2500); }}
      >

        {canScrollLeft && (
          <button
            type="button"
            aria-label="Deslizar hacia la izquierda"
            className="combo-nav-btn"
            style={{ left: '4px' }}
            onClick={() => scrollManual('prev')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="var(--color-verde)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        <div
          ref={scrollRef}
          className="combo-scroll scroll-oculto"
          style={{
            overflowX: hayOverflow ? 'auto' : 'visible',
          }}
        >
          {combos.map((combo, index) => (
            <ComboCard
              key={combo.id}
              combo={combo}
              index={index}
              onClick={() => navigate(`/producto/${combo.id}`, { 
                state: { 
                  producto: combo,
                  esAfiliado
                } 
              })}
            />
          ))}
        </div>

        {canScrollRight && (
          <button
            type="button"
            aria-label="Deslizar hacia la derecha"
            className="combo-nav-btn"
            style={{ right: '4px' }}
            onClick={() => scrollManual('next')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 18l6-6-6-6" stroke="var(--color-verde)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      <style>{`
        .scroll-oculto::-webkit-scrollbar { display: none; }
        .scroll-oculto { -ms-overflow-style: none; scrollbar-width: none; }

        :root {
          --combo-w-mobile: clamp(230px, 74vw, 275px);
          --combo-w-desktop: 225px;
        }

        .combo-scroll {
          display: flex;
          scroll-behavior: smooth;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
        }

        /* Dispositivos móviles: Centrado magnético de tarjetas */
        @media (max-width: 640px) {
          .combo-scroll {
            padding: var(--space-2) calc(50% - (var(--combo-w-mobile) / 2));
            gap: var(--space-4);
          }
          .combo-card {
            min-width: var(--combo-w-mobile) !important;
            max-width: var(--combo-w-mobile) !important;
            scroll-snap-align: center !important;
            scroll-snap-stop: always;
          }
        }

        /* Pantallas grandes / PC */
        @media (min-width: 641px) {
          .combo-scroll {
            padding: var(--space-2) var(--space-4);
            gap: var(--space-5);
            justify-content: ${hayOverflow ? 'flex-start' : 'center'};
          }
          .combo-card {
            min-width: var(--combo-w-desktop) !important;
            max-width: var(--combo-w-desktop) !important;
            scroll-snap-align: start;
          }
        }

        .combo-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 2;
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: 0.5px solid var(--border-default);
          background-color: var(--color-blanco);
          box-shadow: var(--shadow-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }

        .combo-card {
          flex-shrink: 0;
          cursor: pointer;
          background: transparent;
          display: flex;
          flex-direction: column;
        }

        .combo-img-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          flex-shrink: 0;
          background: var(--color-gris-fondo);
        }

        .combo-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: opacity var(--transition-normal), transform var(--transition-slow);
        }

        .combo-card:hover .combo-img {
          transform: scale(1.03);
        }

        .combo-skeleton {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, #f0f0ee 25%, #fff 50%, #f0f0ee 75%);
          background-size: 200% 100%;
          animation: combo-skeleton-loading 1.2s ease-in-out infinite;
        }

        @keyframes combo-skeleton-loading {
          0%   { background-position:  200% 0; }
          100% { background-position: -200% 0; }
        }

        .combo-banda {
          width: 100%;
          background: var(--color-verde);
          padding: var(--space-2) var(--space-3);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .combo-banda-nombre {
          font-family: var(--font-display);
          font-size: var(--font-size-small);
          font-weight: 500;
          letter-spacing: 0.05em;
          color: var(--color-blanco);
          text-align: center;
          line-height: 1.2;
        }

        .combo-body {
          padding: var(--space-2) 0 0;
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }

        .combo-desc {
          margin: 0;
          font-family: var(--font-body);
          font-size: var(--font-size-xs);
          font-weight: var(--font-weight-light);
          color: var(--text-secondary);
          line-height: 1.45;
        }

        .combo-cta {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-family: var(--font-body);
          font-size: var(--font-size-xs);
          font-weight: var(--font-weight-medium);
          color: var(--text-primary);
          transition: color var(--transition-fast), gap var(--transition-fast);
        }

        .combo-card:hover .combo-cta {
          color: var(--color-verde);
          gap: 6px;
        }
      `}</style>
    </div>
  );
};

export default Combos;
